import React from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  Modal,
  OutlinedInput,
  InputAdornment,
  Popover,
} from "@mui/material";
import CopyIcon from "@mui/icons-material/ContentCopy";
import { LoadingPlanDisplay } from "../../contexts/LoadingPlanContext";
import { LoadingPlanStatus, LoadingPlanStatusLabels } from "../../types/loadingPlan";
import "./LoadingPlanCard.scss";
import { loadingPlansService } from "../../services";

interface LoadingPlanCardProps {
  plan: LoadingPlanDisplay;
  onContinue?: (planId: number) => void;
  onView?: (planId: number) => void;
  fetchPlans: () => void;
}

const LoadingPlanCard: React.FC<LoadingPlanCardProps> = ({ plan, onContinue, onView, fetchPlans }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [sharedUrl, setSharedUrl] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleRequestSharedUrl = async () => {
    const url = await loadingPlansService.getSharedUrl(plan.id);
    setSharedUrl(url.shareUrl);
    setIsModalOpen(true);
  };

  const handleCopyToClipboard = (event: any) => {
    navigator.clipboard.writeText(sharedUrl);
    setAnchorEl(event.currentTarget);
    setTimeout(() => setAnchorEl(null), 2000);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handlePublishPlan = async () => {
    await loadingPlansService.updateLoadingPlan(plan.id, {
      status: LoadingPlanStatus.PUBLISHED
    });
    fetchPlans();
  };

  const tags = [];

  if (plan.trailer !== '-') {
    tags.push(`${plan.trailer} trailer`);
  }

  if (plan.includedPartsCount) {
    tags.push(`Parts: ${plan.includedPartsCount}`);
  }

  const getActionButtons = () => {
    switch (plan.status) {
      case LoadingPlanStatus.DRAFT:
      case LoadingPlanStatus.IN_PROGRESS:
        return (
          <Button
            key={2}
            variant="outlined"
            size="small"
            onClick={() => {
              if (onContinue) {
                onContinue(plan.id);
              }
            }}
          >
            CONTINUE
          </Button>
        );
      case LoadingPlanStatus.READY:
        return (<>
          <Button
            key={1}
            variant="outlined"
            size="small"
            onClick={() => {
              if (onContinue) {
                onContinue(plan.id);
              }
            }}
          >
            EDIT
          </Button>
          <Button
            key={1}
            variant="contained"
            size="small"
            onClick={() => { handlePublishPlan() }}
          >
            PUBLISH
          </Button>
        </>);
      case LoadingPlanStatus.LOADED:
      case LoadingPlanStatus.PUBLISHED:
        return (
          <>
            <Button
              key={1}
              variant="outlined"
              size="small"
              onClick={() => {
                if (onView) {
                  onView(plan.id);
                }
              }}
            >
              VIEW
            </Button>
            <Button
              key={2}
              variant="contained"
              size="small"
              onClick={() => { handleRequestSharedUrl() }}
            >
              COPY URL
            </Button>
          </>
        );
      case LoadingPlanStatus.FAILED:
        return (
          <Button
            key={1}
            variant="outlined"
            size="small"
            onClick={() => {
              if (onContinue) {
                onContinue(plan.id);
              }
            }}
          >
            RETRY
          </Button>
        );
      default:
        return [];
    }
  };

  return (
    <Card className="plan-card">
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="shared-url-modal-content">
          <OutlinedInput
            value={sharedUrl}
            endAdornment={<InputAdornment position="end"><CopyIcon className="icon" onClick={(e) => handleCopyToClipboard(e)} /></InputAdornment>}
          />
          <div className="close-button-container">
            <Button onClick={() => setIsModalOpen(false)}>CLOSE</Button>
          </div>
        </div>
      </Modal>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography>Copied</Typography>
        </Box>
      </Popover>
      <CardContent>
        <Box className="plan-head">
          <Box>
            <Typography className="plan-title">
              {plan.name}
            </Typography>
            <Typography className="plan-subtitle">
              {plan.created}
            </Typography>
          </Box>
          <Chip
            label={LoadingPlanStatusLabels[plan.status as LoadingPlanStatus]}
            color={
              plan.status === LoadingPlanStatus.PUBLISHED
                ? "success"
                : plan.status === LoadingPlanStatus.READY
                  ? "info"
                  : plan.status === LoadingPlanStatus.FAILED
                    ? "error"
                    : "default"
            }
            variant="outlined"
            sx={
              plan.status === LoadingPlanStatus.DRAFT
                ? {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                  borderColor: "rgba(0, 0, 0, 0.08)",
                }
                : {}
            }
          />
        </Box>
        <Box className="plan-meta-row">
          <Box className="plan-tags">
            {tags.map((tag, i) => (
              <Chip key={i} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
          <Box className="action-buttons">
            {getActionButtons()}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export { LoadingPlanCard };
